import React, { useMemo } from 'react';
import { FormLabel } from './UI-CHAKRA/FormLabel';
import { CustomMenuItem, CustomSelect } from './UI-CHAKRA/Select/CustomSelect';
import { SelectOption } from '../interfaces/formula';

interface GenericSelectProps {
  value: string | number;
  onChange(val: string | number): void;
  options: string[] | SelectOption[];
  label?: string;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
  onBlur?: () => void;
  onOpen?: () => void;
  onClose?: () => void;
  'data-testid'?: string;
}

function GenericSelect(props: GenericSelectProps) {
  const {
    value,
    onChange,
    options,
    label,
    placeholder = 'Selecione uma opção',
    error,
    disabled,
    required,
    onBlur,
    onOpen,
    onClose,
    'data-testid': dataTestId,
  } = props;

  const menuItems = useMemo(() => {
    return options.map((option, index) => {
      const optionValue = typeof option === 'string' ? option : option.value;
      const optionLabel = typeof option === 'string' ? option : option.label;

      return (
        <CustomMenuItem
          key={`${index}-${optionValue}`}
          onClick={(val) => {
            if (typeof val === 'string' || typeof val === 'number') {
              onChange(val);
            }
          }}
          value={optionValue}
          label={optionLabel}
        />
      );
    });
  }, [onChange, options]);

  const displayLabel = useMemo(() => {
    if (!label) return null;
    return `${label}${required ? '*' : ''}`;
  }, [label, required]);

  const selectedLabel = useMemo(() => {
    if (!value) return undefined;

    if (options.length === 0) return undefined;

    if (typeof options[0] === 'string') {
      const stringOptions = options as string[];
      return stringOptions.find((option) => option === value);
    }

    const objectOptions = options as SelectOption[];
    const selectedOption = objectOptions.find((option) => option.value === value);

    return selectedOption ? selectedOption.label : undefined;
  }, [value, options]);

  return (
    <>
      {displayLabel && <FormLabel>{displayLabel}</FormLabel>}
      <CustomSelect
        placeholder={placeholder}
        value={value}
        displayValue={selectedLabel}
        error={error}
        isDisabled={disabled}
        onBlur={onBlur}
        onOpen={onOpen}
        onClose={onClose}
        data-testid={dataTestId}
      >
        {menuItems}
      </CustomSelect>
    </>
  );
}

export default React.memo(GenericSelect);
